import Link from '@/lib/compat/link';
import { usePathname } from '@/lib/compat/navigation';
import { cn } from '@/lib/utils';
import { isSidebarItemActive, type NavSection } from '@/config/navigation';
import { Badge } from '@/components/ui/badge';
import { useTranslations } from '@/lib/i18n/client';
import { useOrderActionCounts } from '@/lib/hooks/use-order-action-counts';

interface SidebarProps {
  sections: NavSection[];
}

export function Sidebar({ sections }: SidebarProps) {
  const pathname = usePathname();
  const t = useTranslations();
  const { ordersBadge, returnsBadge } = useOrderActionCounts();

  // Map href → badge count for nav items with badge: true
  const badgeCounts: Record<string, number> = {
    '/orders': ordersBadge,
    '/orders/returns': returnsBadge,
  };
  const allItems = sections.flatMap(section => section.items);

  return (
    <aside className="h-full w-56 overflow-y-auto overscroll-y-contain bg-card pe-3 text-sidebar-foreground">
      <nav className="flex flex-col gap-6 py-2">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            {section.titleKey && (
              <h3 className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {t(section.titleKey)}
              </h3>
            )}
            <ul className="flex flex-col gap-1">
              {section.items.map((item) => {
                const isActive = isSidebarItemActive(pathname, item, allItems);
                const Icon = item.icon;
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      aria-current={isActive ? 'page' : undefined}
                      className={cn(
                        // The current page is a filled pill. A start border on a rounded
                        // item rendered as a curved "(" beside the label.
                        'flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-base transition-colors duration-200',
                        isActive
                          ? 'bg-brand-tint text-brand-strong font-semibold'
                          : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground'
                      )}
                    >
                      {Icon && <Icon />}
                      <span className="flex-1">{t(item.labelKey)}</span>
                      {item.badge && (badgeCounts[item.href] ?? 0) > 0 && (
                        <Badge variant="destructive" className="min-w-5 shrink-0 justify-center px-1.5 text-[11px]">
                          {(badgeCounts[item.href] ?? 0) > 99 ? '99+' : badgeCounts[item.href]}
                        </Badge>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
